import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  FileText, Upload, Save, Image, Type, Home, Info, Phone,
  AlertCircle, Loader2, RefreshCw, Layout, Settings, Trash2, Copy,
  Video, ImageIcon, X
} from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Define all editable content with their default values
const SITE_CONTENT_SCHEMA = {
  homepage: {
    label: 'Page d\'accueil',
    icon: Home,
    fields: [
      { key: 'homepage_hero_badge', label: 'Badge hero (texte accroche)', type: 'text', defaultValue: 'Première solution d\'abonnement et de réservation en ligne en France pour l\'entretien de jardin' },
      { key: 'homepage_hero_title_1', label: 'Titre hero ligne 1', type: 'text', defaultValue: 'avoir un' },
      { key: 'homepage_hero_title_highlight', label: 'Titre hero (partie colorée)', type: 'text', defaultValue: 'BEAU JARDIN' },
      { key: 'homepage_hero_title_2', label: 'Titre hero ligne 2', type: 'text', defaultValue: 'c\'est' },
      { key: 'homepage_hero_title_accent', label: 'Titre hero (accent)', type: 'text', defaultValue: 'EASY' },
      { key: 'homepage_hero_description', label: 'Description hero', type: 'textarea', defaultValue: 'Confiez l\'entretien de votre jardin à des professionnels passionnés. Abonnement mensuel ou intervention ponctuelle.' },
      { key: 'homepage_cta_primary', label: 'Bouton principal', type: 'text', defaultValue: 'Créer mon abonnement' },
      { key: 'homepage_cta_secondary', label: 'Bouton secondaire', type: 'text', defaultValue: 'Intervention ponctuelle' },
      { key: 'homepage_stat_1_value', label: 'Stat 1 - Valeur', type: 'text', defaultValue: '10+' },
      { key: 'homepage_stat_1_label', label: 'Stat 1 - Label', type: 'text', defaultValue: 'Années d\'expérience' },
      { key: 'homepage_stat_2_value', label: 'Stat 2 - Valeur', type: 'text', defaultValue: '500+' },
      { key: 'homepage_stat_2_label', label: 'Stat 2 - Label', type: 'text', defaultValue: 'Jardins entretenus' },
      { key: 'homepage_stat_3_value', label: 'Stat 3 - Valeur', type: 'text', defaultValue: '98%' },
      { key: 'homepage_stat_3_label', label: 'Stat 3 - Label', type: 'text', defaultValue: 'Clients satisfaits' },
      { key: 'homepage_stat_4_value', label: 'Stat 4 - Valeur', type: 'text', defaultValue: '24h' },
      { key: 'homepage_stat_4_label', label: 'Stat 4 - Label', type: 'text', defaultValue: 'Délai intervention' },
      { key: 'homepage_features_title', label: 'Section caractéristiques - Titre', type: 'text', defaultValue: 'L\'excellence au service de votre jardin' },
      { key: 'homepage_services_title', label: 'Section services - Titre', type: 'text', defaultValue: 'Des services adaptés à tous vos besoins' },
      { key: 'homepage_reviews_title', label: 'Section avis - Titre', type: 'text', defaultValue: 'Ce que nos clients disent' },
      { key: 'homepage_reviews_rating', label: 'Note moyenne affichée', type: 'text', defaultValue: '4.9/5 basé sur 127 avis' },
      { key: 'homepage_cta_title', label: 'Section CTA - Titre', type: 'text', defaultValue: 'Prêt à transformer votre jardin ?' },
      { key: 'homepage_cta_description', label: 'Section CTA - Description', type: 'textarea', defaultValue: 'Contactez-nous dès aujourd\'hui pour un devis gratuit et personnalisé. Notre équipe vous accompagne dans tous vos projets d\'entretien.' },
      { key: 'homepage_blog_title', label: 'Section blog - Titre', type: 'text', defaultValue: 'Nos derniers articles' },
    ]
  },
  media_config: {
    label: 'Vidéo & Médias',
    icon: Video,
    fields: [
      { key: 'global_youtube_video_id', label: 'ID Vidéo YouTube (hero background)', type: 'text', defaultValue: 'g_NuDrtC9RM', hint: 'L\'ID se trouve dans l\'URL YouTube après "v=" (ex: youtube.com/watch?v=g_NuDrtC9RM)' },
      { key: 'global_hero_fallback_image', label: 'Image de fond hero (fallback mobile)', type: 'image', defaultValue: 'https://images.pexels.com/photos/5082949/pexels-photo-5082949.jpeg?auto=compress&cs=tinysrgb&w=1920' },
      { key: 'credit_impot_video_id', label: 'ID Vidéo YouTube (page crédit impôt)', type: 'text', defaultValue: '', hint: 'Laissez vide pour ne pas afficher de vidéo' },
    ]
  },
  credit_impot: {
    label: 'Crédit d\'impôt',
    icon: Info,
    fields: [
      { key: 'credit_impot_title', label: 'Titre principal', type: 'text', defaultValue: 'Crédit d\'impôts 50%' },
      { key: 'credit_impot_subtitle', label: 'Sous-titre', type: 'text', defaultValue: 'Économisez sur l\'entretien de votre jardin' },
      { key: 'credit_impot_intro', label: 'Introduction', type: 'textarea', defaultValue: 'Bénéficiez de l\'avance immédiate de crédit d\'impôts sur toutes vos prestations de jardinage éligibles.' },
      { key: 'credit_impot_how_title', label: 'Comment ça marche - Titre', type: 'text', defaultValue: 'Comment ça marche ?' },
      { key: 'credit_impot_step_1', label: 'Étape 1', type: 'text', defaultValue: 'Réservez votre prestation en ligne' },
      { key: 'credit_impot_step_2', label: 'Étape 2', type: 'text', defaultValue: 'Ne payez que 50% du montant' },
      { key: 'credit_impot_step_3', label: 'Étape 3', type: 'text', defaultValue: 'Le crédit d\'impôts est déduit automatiquement' },
      { key: 'credit_impot_eligible_title', label: 'Services éligibles - Titre', type: 'text', defaultValue: 'Services éligibles au crédit d\'impôts' },
      { key: 'credit_impot_cta', label: 'Bouton CTA', type: 'text', defaultValue: 'Réserver maintenant' },
    ]
  },
  contact: {
    label: 'Contact',
    icon: Phone,
    fields: [
      { key: 'contact_title', label: 'Titre', type: 'text', defaultValue: 'Contactez-nous' },
      { key: 'contact_subtitle', label: 'Sous-titre', type: 'text', defaultValue: 'Une question ? Besoin d\'un devis ? Notre équipe est à votre écoute.' },
      { key: 'contact_address', label: 'Adresse', type: 'text', defaultValue: 'Quartier le Paty, 13800 Istres' },
      { key: 'contact_phone', label: 'Téléphone', type: 'text', defaultValue: '04 65 01 10 22' },
      { key: 'contact_email', label: 'Email', type: 'text', defaultValue: 'contact@jardily.fr' },
      { key: 'contact_hours_title', label: 'Horaires - Titre', type: 'text', defaultValue: 'Horaires d\'ouverture' },
      { key: 'contact_hours_weekday', label: 'Horaires semaine', type: 'text', defaultValue: 'Lundi - Vendredi : 9h - 12h / 13h - 17h' },
      { key: 'contact_hours_weekend', label: 'Horaires weekend', type: 'text', defaultValue: 'Samedi - Dimanche : Fermé' },
      { key: 'contact_form_title', label: 'Titre formulaire', type: 'text', defaultValue: 'Envoyez-nous un message' },
      { key: 'contact_form_cta', label: 'Bouton formulaire', type: 'text', defaultValue: 'Envoyer le message' },
    ]
  },
  services: {
    label: 'Services',
    icon: Settings,
    fields: [
      { key: 'services_title', label: 'Titre principal', type: 'text', defaultValue: 'Nos services d\'entretien de jardin' },
      { key: 'services_subtitle', label: 'Sous-titre', type: 'textarea', defaultValue: 'Des prestations professionnelles pour sublimer votre espace extérieur' },
      { key: 'services_credit_title', label: 'Section crédit impôt - Titre', type: 'text', defaultValue: 'Services éligibles au crédit d\'impôt 50%' },
      { key: 'services_credit_subtitle', label: 'Section crédit impôt - Sous-titre', type: 'text', defaultValue: 'Bénéficiez de l\'avance immédiate : vous ne payez que la moitié !' },
      { key: 'services_conseil_title', label: 'Section conseil - Titre', type: 'text', defaultValue: 'Conseils & Études' },
      { key: 'services_conseil_subtitle', label: 'Section conseil - Sous-titre', type: 'text', defaultValue: 'Nos experts vous accompagnent dans la conception et l\'aménagement de votre jardin' },
      { key: 'services_amenagement_title', label: 'Section aménagement - Titre', type: 'text', defaultValue: 'Aménagements & Installations' },
      { key: 'services_amenagement_subtitle', label: 'Section aménagement - Sous-titre', type: 'text', defaultValue: 'De la création de jardin à l\'installation de terrasses, nous réalisons tous vos projets d\'aménagement extérieur.' },
      { key: 'services_cta_title', label: 'CTA - Titre', type: 'text', defaultValue: 'Prêt à transformer votre jardin ?' },
      { key: 'services_cta_description', label: 'CTA - Description', type: 'text', defaultValue: 'Contactez-nous pour un devis gratuit ou créez directement votre abonnement en ligne.' },
    ]
  },
  global: {
    label: 'Global',
    icon: Layout,
    fields: [
      { key: 'global_company_name', label: 'Nom de l\'entreprise', type: 'text', defaultValue: 'Jardily' },
      { key: 'global_slogan', label: 'Slogan', type: 'text', defaultValue: 'avoir un BEAU JARDIN c\'est EASY' },
      { key: 'global_phone', label: 'Téléphone', type: 'text', defaultValue: '04 65 01 10 22' },
      { key: 'global_email', label: 'Email', type: 'text', defaultValue: 'contact@jardily.fr' },
      { key: 'global_address', label: 'Adresse complète', type: 'text', defaultValue: 'Quartier le Paty, 13800 Istres, Provence' },
      { key: 'global_footer_description', label: 'Description footer', type: 'textarea', defaultValue: 'Votre partenaire pour un jardin impeccable à Istres et ses environs.' },
    ]
  }
};

const AdminSiteContent = () => {
  const [activeTab, setActiveTab] = useState('homepage');
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Legal pages state
  const [selectedLegal, setSelectedLegal] = useState('mentions-legales');
  const [legalContent, setLegalContent] = useState({ title: '', content: '' });

  // Media library state
  const [mediaFiles, setMediaFiles] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePickerField, setImagePickerField] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchAllContent();
    fetchMediaFiles();
  }, []);

  useEffect(() => {
    if (selectedLegal) {
      fetchLegalPage(selectedLegal);
    }
  }, [selectedLegal]);

  const fetchAllContent = async () => {
    try {
      const res = await axios.get(`${API}/content`);
      const contentMap = {};
      res.data.forEach(item => {
        contentMap[item.key] = item.content;
      });
      setContent(contentMap);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMediaFiles = async () => {
    setLoadingMedia(true);
    try {
      const res = await axios.get(`${API}/media`);
      setMediaFiles(res.data);
    } catch (error) {
      console.error('Error fetching media:', error);
    } finally {
      setLoadingMedia(false);
    }
  };

  const fetchLegalPage = async (slug) => {
    try {
      const res = await axios.get(`${API}/legal-pages/${slug}`);
      setLegalContent({ title: res.data.title || '', content: res.data.content || '' });
    } catch (error) {
      console.error('Error fetching legal page:', error);
      setLegalContent({ title: '', content: '' });
    }
  };

  const handleContentChange = (key, value) => {
    setContent(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const getValue = (key, defaultValue) => {
    return content[key] !== undefined ? content[key] : defaultValue;
  };

  const saveContent = async () => {
    setSaving(true);
    const token = localStorage.getItem('jardily_token');
    
    try {
      const items = [];
      Object.entries(SITE_CONTENT_SCHEMA).forEach(([page, config]) => {
        config.fields.forEach(field => {
          items.push({
            key: field.key,
            content: getValue(field.key, field.defaultValue),
            content_type: field.type === 'textarea' ? 'html' : field.type === 'image' ? 'image' : 'text',
            page: page
          });
        });
      });

      await axios.post(`${API}/content/batch`, items, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Contenu sauvegardé avec succès !');
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const saveLegalPage = async () => {
    setSaving(true);
    const token = localStorage.getItem('jardily_token');
    try {
      await axios.put(`${API}/legal-pages/${selectedLegal}`, legalContent, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Page légale sauvegardée !');
    } catch (error) {
      console.error('Error saving legal page:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const token = localStorage.getItem('jardily_token');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Image uploadée !');
      fetchMediaFiles(); // Refresh media library
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const deleteMedia = async (filename) => {
    if (!window.confirm('Supprimer cette image ?')) return;
    
    const token = localStorage.getItem('jardily_token');
    try {
      await axios.delete(`${API}/media/${filename}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Image supprimée');
      fetchMediaFiles();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const copyImageUrl = (url) => {
    const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${url}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success('URL copiée !');
  };

  const selectImageForField = (url) => {
    if (imagePickerField) {
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${url}`;
      handleContentChange(imagePickerField, fullUrl);
      setImagePickerField(null);
      toast.success('Image sélectionnée !');
    }
  };

  const legalPageNames = {
    'mentions-legales': 'Mentions légales',
    'politique-confidentialite': 'Politique de confidentialité',
    'cgv': 'Conditions Générales de Vente'
  };

  const renderField = (field) => {
    const value = getValue(field.key, field.defaultValue);
    
    if (field.type === 'image') {
      return (
        <div key={field.key} className="space-y-2">
          <Label className="text-sm font-medium text-stone-700">{field.label}</Label>
          <div className="flex gap-2">
            <Input
              value={value}
              onChange={(e) => handleContentChange(field.key, e.target.value)}
              placeholder={field.defaultValue}
              className="flex-1"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => setImagePickerField(field.key)}
              className="shrink-0"
            >
              <ImageIcon className="w-4 h-4 mr-2" />
              Choisir
            </Button>
          </div>
          {value && (
            <img src={value} alt="Preview" className="h-20 w-auto rounded-lg object-cover mt-2" />
          )}
        </div>
      );
    }
    
    if (field.type === 'textarea') {
      return (
        <div key={field.key} className="space-y-2">
          <Label className="text-sm font-medium text-stone-700">{field.label}</Label>
          <Textarea
            value={value}
            onChange={(e) => handleContentChange(field.key, e.target.value)}
            rows={3}
            className="w-full"
            placeholder={field.defaultValue}
          />
        </div>
      );
    }

    return (
      <div key={field.key} className="space-y-2">
        <Label className="text-sm font-medium text-stone-700">{field.label}</Label>
        <Input
          value={value}
          onChange={(e) => handleContentChange(field.key, e.target.value)}
          placeholder={field.defaultValue}
        />
        {field.hint && (
          <p className="text-xs text-stone-500">{field.hint}</p>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading font-bold text-2xl text-stone-900">Gestion du contenu</h1>
            <p className="text-stone-600 text-sm mt-1">
              Modifiez tous les textes, images et vidéos de votre site
            </p>
          </div>
          {hasChanges && (
            <div className="flex items-center gap-3">
              <span className="text-amber-600 text-sm flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                Modifications non sauvegardées
              </span>
              <Button onClick={saveContent} disabled={saving} className="btn-primary">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sauvegarde...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Sauvegarder tout
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-stone-100 p-1 rounded-xl flex-wrap h-auto gap-1">
            {Object.entries(SITE_CONTENT_SCHEMA).map(([key, config]) => {
              const Icon = config.icon;
              return (
                <TabsTrigger 
                  key={key} 
                  value={key} 
                  className="rounded-lg data-[state=active]:bg-white"
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {config.label}
                </TabsTrigger>
              );
            })}
            <TabsTrigger value="legal" className="rounded-lg data-[state=active]:bg-white">
              <FileText className="w-4 h-4 mr-2" />
              Pages légales
            </TabsTrigger>
            <TabsTrigger value="media" className="rounded-lg data-[state=active]:bg-white">
              <Image className="w-4 h-4 mr-2" />
              Médiathèque
            </TabsTrigger>
          </TabsList>

          {/* Dynamic content tabs */}
          {Object.entries(SITE_CONTENT_SCHEMA).map(([key, config]) => (
            <TabsContent key={key} value={key} className="space-y-6">
              <div className="bg-white rounded-2xl border border-stone-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-semibold text-lg text-stone-900 flex items-center gap-2">
                    {React.createElement(config.icon, { className: "w-5 h-5 text-emerald-600" })}
                    {config.label}
                  </h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      config.fields.forEach(field => {
                        handleContentChange(field.key, field.defaultValue);
                      });
                    }}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Réinitialiser
                  </Button>
                </div>
                
                <div className="grid gap-4 md:grid-cols-2">
                  {config.fields.map(field => renderField(field))}
                </div>

                <div className="mt-6 flex justify-end">
                  <Button onClick={saveContent} disabled={saving} className="btn-primary">
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sauvegarde...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Sauvegarder
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </TabsContent>
          ))}

          {/* Legal Pages Tab */}
          <TabsContent value="legal" className="space-y-6">
            <div className="bg-white rounded-2xl border border-stone-200 p-6">
              <div className="flex flex-wrap gap-2 mb-6">
                {Object.entries(legalPageNames).map(([slug, name]) => (
                  <button
                    key={slug}
                    onClick={() => setSelectedLegal(slug)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                      selectedLegal === slug
                        ? 'bg-emerald-600 text-white'
                        : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                    }`}
                  >
                    {name}
                  </button>
                ))}
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Titre de la page</Label>
                  <Input
                    value={legalContent.title}
                    onChange={(e) => setLegalContent({ ...legalContent, title: e.target.value })}
                    placeholder="Titre de la page"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Contenu (HTML)</Label>
                  <Textarea
                    value={legalContent.content}
                    onChange={(e) => setLegalContent({ ...legalContent, content: e.target.value })}
                    placeholder="<h2>Titre</h2><p>Contenu...</p>"
                    rows={20}
                    className="mt-1 font-mono text-sm"
                  />
                </div>

                <div className="flex justify-end">
                  <Button onClick={saveLegalPage} disabled={saving} className="btn-primary">
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sauvegarde...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Sauvegarder
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Media Library Tab */}
          <TabsContent value="media" className="space-y-6">
            <div className="bg-white rounded-2xl border border-stone-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-lg text-stone-900">Médiathèque</h3>
                <Button onClick={fetchMediaFiles} variant="outline" size="sm">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Actualiser
                </Button>
              </div>
              
              {/* Upload area */}
              <div className="border-2 border-dashed border-stone-300 rounded-xl p-8 text-center mb-6">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="image-upload"
                />
                <label htmlFor="image-upload" className="cursor-pointer flex flex-col items-center">
                  {uploading ? (
                    <Loader2 className="w-12 h-12 text-emerald-600 animate-spin mb-4" />
                  ) : (
                    <Upload className="w-12 h-12 text-stone-400 mb-4" />
                  )}
                  <span className="text-stone-700 font-medium">
                    {uploading ? 'Upload en cours...' : 'Cliquez pour uploader une image'}
                  </span>
                  <span className="text-stone-500 text-sm mt-1">
                    JPG, PNG, WEBP ou GIF (max 10MB)
                  </span>
                </label>
              </div>

              {/* Media grid */}
              {loadingMedia ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
                </div>
              ) : mediaFiles.length === 0 ? (
                <div className="text-center py-12 text-stone-500">
                  <Image className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>Aucune image dans la médiathèque</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {mediaFiles.map((file) => (
                    <div key={file.filename} className="relative group aspect-square">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${file.url}`}
                        alt={file.filename}
                        className="w-full h-full object-cover rounded-xl"
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-2">
                        <button
                          onClick={() => copyImageUrl(file.url)}
                          className="p-2 bg-white rounded-lg hover:bg-stone-100 transition-colors"
                          title="Copier l'URL"
                        >
                          <Copy className="w-4 h-4 text-stone-700" />
                        </button>
                        <button
                          onClick={() => deleteMedia(file.filename)}
                          className="p-2 bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4 text-white" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Utilisation des images</p>
                    <p>Cliquez sur l'icône copier pour obtenir l'URL d'une image, puis collez-la dans les champs image des services, blog, ou dans l'onglet "Vidéo & Médias" pour les fonds de page.</p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Image Picker Dialog */}
        <Dialog open={!!imagePickerField} onOpenChange={() => setImagePickerField(null)}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Sélectionner une image</DialogTitle>
            </DialogHeader>
            {mediaFiles.length === 0 ? (
              <div className="text-center py-12 text-stone-500">
                <p>Aucune image disponible. Uploadez d'abord des images dans la médiathèque.</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-4 gap-4 mt-4">
                {mediaFiles.map((file) => (
                  <button
                    key={file.filename}
                    onClick={() => selectImageForField(file.url)}
                    className="aspect-square rounded-xl overflow-hidden hover:ring-4 hover:ring-emerald-500 transition-all"
                  >
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${file.url}`}
                      alt={file.filename}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminSiteContent;
